import talk2Api from 'src/plugins/api/api'
import { CALENDAR_OAUTH_MESSAGE_SOURCE, CalendarOAuthPopupStatus } from 'src/constants/calendar-connections'

const POPUP_CLOSED_POLL_INTERVAL_MILLISECONDS = 500
const POPUP_WIDTH = 600
const POPUP_HEIGHT = 700
const POPUP_LEFT = 200
const POPUP_TOP = 100
const POPUP_WINDOW_FEATURES = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${POPUP_LEFT},top=${POPUP_TOP}`
const OAUTH_POPUP_TIMEOUT_MILLISECONDS = 5 * 60 * 1000

export default {
  methods: {
    /**
     * Opens the driver OAuth authorization URL in a popup and resolves once the connection
     * is created.
     *
     * api-core's callback view postMessages the result to the origin that started the flow,
     * carried through the signed OAuth state and validated against an allowlist that includes
     * Talk's own origin (config/calendarv2.php `allowed_oauth_return_origins`), so both the
     * CONNECTED and the ERROR branch below do fire for Talk.
     *
     * The popup-closed poll is the fallback for the cases where no message arrives at all: the
     * window closed before its script ran, or the provider never reached api-core. Nothing
     * distinguishes those from the agent closing the window on purpose, so that path reports
     * that nothing was connected rather than naming a cause.
     */
    openCalendarOAuthPopup (driver, visibility, existingConnections) {
      return new Promise((resolve, reject) => {
        const popup = window.open('about:blank', 'calendar_oauth_popup', POPUP_WINDOW_FEATURES)

        if (!popup) {
          reject(new Error('We couldn\'t open the calendar connection window. Please allow pop-ups and try again.'))
          return
        }

        const existingConnectionIds = existingConnections.map(connection => connection.id)
        const allowedMessageOrigin = new URL(process.env.API_URL).origin

        let messageListener = null
        let pollIntervalId = null
        let timeoutId = null
        let settled = false

        const teardown = () => {
          if (messageListener) {
            window.removeEventListener('message', messageListener)
            messageListener = null
          }

          if (pollIntervalId) {
            clearInterval(pollIntervalId)
            pollIntervalId = null
          }

          if (timeoutId) {
            clearTimeout(timeoutId)
            timeoutId = null
          }
        }

        const settleResolve = (connection) => {
          if (settled) {
            return
          }

          settled = true
          teardown()

          if (popup && !popup.closed) {
            popup.close()
          }

          resolve(connection)
        }

        const settleReject = (message) => {
          if (settled) {
            return
          }

          settled = true
          teardown()

          if (popup && !popup.closed) {
            popup.close()
          }

          reject(new Error(message))
        }

        timeoutId = setTimeout(() => {
          settleReject('Calendar connection timed out. Please try again.')
        }, OAUTH_POPUP_TIMEOUT_MILLISECONDS)

        const findNewConnection = async () => {
          const response = await talk2Api.V2.calendarConnections.get()
          const connections = response.data.data

          return connections.find(connection =>
            !existingConnectionIds.includes(connection.id) &&
            connection.driver === driver &&
            connection.visibility === visibility
          )
        }

        // A failure to read the connections back is a failure of the flow, not a cancellation.
        // Resolving null here reported "connection cancelled" for a dropped network, which is the
        // silent failure this flow had.
        const settleFromConnections = () => {
          findNewConnection()
            .then(connection => settleResolve(connection || null))
            .catch(() => settleReject('We couldn\'t confirm the calendar connection. Check your network and try again.'))
        }

        messageListener = (event) => {
          if (event.origin !== allowedMessageOrigin) {
            return
          }

          if (!event.data || event.data.source !== CALENDAR_OAUTH_MESSAGE_SOURCE) {
            return
          }

          if (event.data.status === CalendarOAuthPopupStatus.ERROR) {
            settleReject(event.data.message || 'The calendar connection could not be completed.')
            return
          }

          if (event.data.status === CalendarOAuthPopupStatus.CONNECTED) {
            settleFromConnections()
          }
        }

        window.addEventListener('message', messageListener)

        pollIntervalId = setInterval(() => {
          if (!popup || !popup.closed) {
            return
          }

          clearInterval(pollIntervalId)
          pollIntervalId = null

          settleFromConnections()
        }, POPUP_CLOSED_POLL_INTERVAL_MILLISECONDS)

        talk2Api.V2.calendarConnections.getAuthorizationUrl(driver, visibility)
          .then(response => {
            if (popup) {
              popup.location = response.data.data.url
            }
          })
          .catch(error => {
            settleReject(error.response?.data?.error || 'Unable to start the calendar connection. Please try again.')
          })
      })
    }
  }
}
